import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useStepper } from "@/components/ui/stepper";
import { TextareaAutosize } from "@/components/ui/textarea-autosize";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLocalStorage } from "@/hooks/useStorage";
import { useTranslation } from "@/hooks/useTranslation";
import { useSetupSurveyOptions } from "@/lib/setup-survey";
import { zodResolver } from "@hookform/resolvers/zod";
import { SetupTeamCreationSurveyDTO } from "@shaarp/common";
import { HelpCircleIcon } from "lucide-react";
import { useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { FiChevronRight } from "react-icons/fi";
import * as z from "zod/v3";

const FormSchema = z.object({
  comeFrom: z.array(z.string()).nonempty({
    message: "Veuillez sélectionner au moins une option.",
  }),
  comeFromOther: z.string().optional(),
  potentialUsersCount: z.string().min(1, {
    message: "Veuillez renseigner le nombre d'utilisateurs potentiels.",
  }),
  whatWaitingFor: z.array(z.string()).nonempty({
    message: "Veuillez sélectionner au moins une option.",
  }),
  whatWaitingForOther: z.string().optional(),
  comment: z.string().optional(),
});

export const SetupTeamCreationSurveyPage = () => {
  const { nextStep, prevStep } = useStepper();
  const { t } = useTranslation();
  const [setupTeamCreationSurveyDatas, setSetupTeamCreationSurveyDatas] =
    useLocalStorage<SetupTeamCreationSurveyDTO>("setup-team-creation-survey", {
      comeFrom: [],
      comeFromOther: "",
      potentialUsersCount: "",
      whatWaitingFor: [],
      whatWaitingForOther: "",
      comment: "",
    });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema as any) as any,
    defaultValues: {
      comeFrom: setupTeamCreationSurveyDatas?.comeFrom || [],
      comeFromOther: setupTeamCreationSurveyDatas?.comeFromOther || "",
      potentialUsersCount:
        setupTeamCreationSurveyDatas?.potentialUsersCount || "",
      whatWaitingFor: setupTeamCreationSurveyDatas?.whatWaitingFor || [],
      whatWaitingForOther:
        setupTeamCreationSurveyDatas?.whatWaitingForOther || "",
      comment: setupTeamCreationSurveyDatas?.comment || "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    setSetupTeamCreationSurveyDatas({
      comeFrom: values.comeFrom,
      comeFromOther: values.comeFromOther || "",
      potentialUsersCount: values.potentialUsersCount,
      whatWaitingFor: values.whatWaitingFor,
      whatWaitingForOther: values.whatWaitingForOther || "",
      comment: values.comment || "",
    });

    nextStep();
  };

  return (
    <div className="flex flex-col gap-7">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-primary">
          {t("setup.teamCreation.survey.title")}
        </h1>
        <p className="text-sm">{t("setup.teamCreation.survey.description")}</p>
      </div>
      <div className="flex flex-col gap-5 items-center">
        <Form {...form}>
          <form className="space-y-6">
            <FieldComeFrom form={form} />
            <FieldPotentialUsersCount form={form} />
            <FieldWhatWaitingFor form={form} />
            <FieldComment form={form} />
          </form>
        </Form>
        <div className="space-x-2">
          <Button onClick={() => prevStep()} variant="outline">
            {t("setup.teamCreation.survey.buttons.previous")}
          </Button>
          <Button onClick={form.handleSubmit(onSubmit)}>
            {t("setup.teamCreation.survey.buttons.continue")}
            <FiChevronRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const FieldComeFrom = ({
  form,
}: {
  form: UseFormReturn<z.infer<typeof FormSchema>>;
}) => {
  const { t } = useTranslation();
  const { comeFromOptions } = useSetupSurveyOptions();
  const [showComeFromOther, setShowComeFromOther] = useState(
    form.getValues().comeFrom.includes("other"),
  );

  const toggleOption = (e: any, option: string) => {
    e.preventDefault();
    const currentOptions = form.getValues().comeFrom;
    const updatedFrom = currentOptions.includes(option)
      ? currentOptions.filter((o) => o !== option)
      : [...currentOptions, option];

    form.setValue("comeFrom", updatedFrom as [string, ...string[]]);
    setShowComeFromOther(updatedFrom.includes("other"));
  };

  return (
    <>
      <FormField
        control={form.control}
        name="comeFrom"
        render={() => (
          <FormItem>
            <FormLabel className="text-lg font-semibold">
              {t("setup.teamCreation.survey.fields.comeFrom.label")}
            </FormLabel>
            <FormControl>
              <div className="flex flex-wrap gap-2">
                {comeFromOptions.map((option) => (
                  <Button
                    key={option.value}
                    onClick={(e) => toggleOption(e, option.value)}
                    variant={
                      form.getValues().comeFrom.includes(option.value)
                        ? "default"
                        : "outline"
                    }
                  >
                    {option.text}
                  </Button>
                ))}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {showComeFromOther && (
        <FormField
          control={form.control}
          name="comeFromOther"
          render={() => (
            <FormItem>
              <Input
                type="text"
                placeholder={t(
                  "setup.teamCreation.survey.fields.comeFrom.placeholder",
                )}
                value={form.getValues().comeFromOther}
                onChange={(e) => form.setValue("comeFromOther", e.target.value)}
                className="mt-2 bg-background"
              />
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </>
  );
};

const FieldPotentialUsersCount = ({
  form,
}: {
  form: UseFormReturn<z.infer<typeof FormSchema>>;
}) => {
  const { t } = useTranslation();
  return (
    <FormField
      control={form.control}
      name="potentialUsersCount"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-lg font-semibold">
            {t("setup.teamCreation.survey.fields.potentialUsersCount.label")}
          </FormLabel>
          <FormControl>
            <Select
              value={field.value}
              onValueChange={(value) =>
                form.setValue("potentialUsersCount", value)
              }
            >
              <SelectTrigger className="w-full bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1-2">1-2</SelectItem>
                <SelectItem value="2-5">2-5</SelectItem>
                <SelectItem value="5-10">5-10</SelectItem>
                <SelectItem value="10-20">10-20</SelectItem>
                <SelectItem value="20-50">20-50</SelectItem>
                <SelectItem value="+50">+50</SelectItem>
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

const FieldWhatWaitingFor = ({
  form,
}: {
  form: UseFormReturn<z.infer<typeof FormSchema>>;
}) => {
  const { t } = useTranslation();
  const { whatWaitingForOptions } = useSetupSurveyOptions();
  const [showWhatWaitingForOther, setShowWhatWaitingForOther] = useState(
    form.getValues().whatWaitingFor.includes("other"),
  );

  const toggleOption = (e: any, option: string) => {
    e.preventDefault();
    const currentOptions = form.getValues().whatWaitingFor;
    const updatedOptions = currentOptions.includes(option)
      ? currentOptions.filter((o) => o !== option)
      : [...currentOptions, option];

    form.setValue("whatWaitingFor", updatedOptions as [string, ...string[]]);
    setShowWhatWaitingForOther(updatedOptions.includes("other"));
  };

  return (
    <>
      <FormField
        control={form.control}
        name="whatWaitingFor"
        render={() => (
          <FormItem>
            <FormLabel className="text-lg font-semibold">
              {t("setup.teamCreation.survey.fields.whatWaitingFor.label")}
            </FormLabel>
            <FormControl>
              <div className="flex flex-wrap gap-2">
                {whatWaitingForOptions.map((option) => (
                  <Button
                    key={option.value}
                    onClick={(e) => toggleOption(e, option.value)}
                    variant={
                      form.getValues().whatWaitingFor.includes(option.value)
                        ? "default"
                        : "outline"
                    }
                  >
                    {option.text}
                  </Button>
                ))}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {showWhatWaitingForOther && (
        <FormField
          control={form.control}
          name="whatWaitingForOther"
          render={() => (
            <FormItem>
              <Input
                type="text"
                placeholder={t(
                  "setup.teamCreation.survey.fields.whatWaitingFor.placeholder",
                )}
                value={form.getValues().whatWaitingForOther}
                onChange={(e) =>
                  form.setValue("whatWaitingForOther", e.target.value)
                }
                className="mt-2 bg-background"
              />
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </>
  );
};

const FieldComment = ({
  form,
}: {
  form: UseFormReturn<z.infer<typeof FormSchema>>;
}) => {
  const { t } = useTranslation();
  return (
    <FormField
      control={form.control}
      name="comment"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-lg font-semibold">
            {t("setup.teamCreation.survey.fields.comment.label")}
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircleIcon className="inline-block ml-2 w-3 h-3" />
              </TooltipTrigger>
              <TooltipContent>
                {t("setup.teamCreation.survey.fields.comment.tooltip")}
              </TooltipContent>
            </Tooltip>
          </FormLabel>
          <FormControl>
            <TextareaAutosize {...field} className="bg-background" />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
